import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  CreditCard,
  HelpCircleIcon,
  LogOut,
  MessageCircle,
  PanelLeft,
  Sparkles,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useAdminImpersonation } from "@/hooks/useAdminImpersonation";
import { usePageTitle } from "@/components/layout/hooks/usePageTitle";
import { useLogout } from "@/hooks/useLogout";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useLayoutRole } from "@/hooks/useLayoutRole";
import { Notifications } from "./Notifications";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Role } from "@/types/user";
import { GlobalSearch } from "@/components/layout/components/global-search/GlobalSearch";
import { useHotel } from "@/hooks/useHotel";
import { useCurrentUserAppLinks } from "@/hooks/useAppLinks";
import { PlatformRole } from "@/utils/accessControl";

interface HeaderProps {
  onOpenSidebar: () => void;
  sidebarOpen?: boolean;
  onToggle: () => void;
}

export const Header: React.FC<HeaderProps> = ({ sidebarOpen, onToggle }) => {
  const { t } = useTranslation();
  const pageTitle = usePageTitle();
  const { userProfile } = useAuthenticatedUser();
  const layoutRole = useLayoutRole();

  const isPlatformAdmin =
    layoutRole === PlatformRole.SUPER_ADMIN ||
    layoutRole === PlatformRole.GUESTERIA_ADMIN;

  const { hotelId } = useHotel(layoutRole === "HOTEL_SCOPED");

  const flatProfile = userProfile as
    | (Record<string, unknown> & { firstName?: string; lastName?: string })
    | null;
  const firstName = userProfile?.profile?.firstName || flatProfile?.firstName;
  const lastName = userProfile?.profile?.lastName || flatProfile?.lastName;

  const userName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : firstName || t("layout.userFallback");
  const userRole = userProfile?.role;

  const currentPlanTitle =
    userProfile?.access?.currentPlan?.title?.trim() ?? "";
  const shouldShowCurrentPlan = !isPlatformAdmin && currentPlanTitle.length > 0;
  const shouldShowUpgradeToPro = !isPlatformAdmin && !shouldShowCurrentPlan;

  const getRoleLabel = (role: string | null): string => {
    if (!role) return "";
    switch (role) {
      case PlatformRole.SUPER_ADMIN:
        return t("layout.roles.SUPER_ADMIN");
      case PlatformRole.GUESTERIA_ADMIN:
        return t("layout.roles.GUESTERIA_ADMIN", "Guesteria Admin");
      case Role.TENANT_ADMIN:
        return t("layout.roles.TENANT_ADMIN");
      case Role.HOTEL_ADMIN:
        return t("layout.roles.HOTEL_ADMIN");
      case Role.FRONT_DESK:
        return t("layout.roles.FRONT_DESK");
      case Role.STAFF:
        return t("layout.roles.STAFF");
      default:
        return role;
    }
  };

  const userRoleLabel = getRoleLabel(userRole ?? null);

  const getUserInitials = (): string => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    return "";
  };

  const userInitials = getUserInitials();

  const navigate = useNavigate();
  const { handleLogout } = useLogout();
  const { exitImpersonation, isImpersonating } = useAdminImpersonation();
  const signOutLabel = isImpersonating
    ? t("layout.userMenu.exitImpersonation")
    : t("layout.userMenu.signOut");
  const handleAuthAction = isImpersonating ? exitImpersonation : handleLogout;
  const { data: appLinks } = useCurrentUserAppLinks();

  const openExternalLink = (url: string | null) => {
    if (!url) {
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <header className="font-geist border-b-header sticky top-0 z-40 grid min-h-16 grid-cols-12 grid-rows-1 gap-0 border-b bg-white px-4 py-1">
      <div
        className={
          "col-span-3 flex min-w-0 flex-row items-center justify-start gap-4 p-3"
        }
      >
        <Button variant="new_ghost" size="icon" onClick={onToggle}>
          <PanelLeft className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="!h-4" />
        <h4
          className={cn(
            "font-geist text-card-foreground scroll-m-20 truncate text-sm leading-normal font-normal whitespace-nowrap",
            {
              "ml-2": !sidebarOpen,
            },
          )}
        >
          {pageTitle}
        </h4>
      </div>
      <div className="col-span-4 col-start-4 flex items-center 2xl:col-span-5 2xl:col-start-4">
        <GlobalSearch />
      </div>
      <div
        className={
          "col-span-3 col-start-10 flex flex-row items-center justify-end gap-6"
        }
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("layout.helpMenu.label")}
            >
              <HelpCircleIcon className="size-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuGroup>
              <DropdownMenuItem
                disabled={!appLinks?.knowledgeBase}
                onClick={() =>
                  openExternalLink(appLinks?.knowledgeBase ?? null)
                }
              >
                <HelpCircleIcon className="text-zinc-500" />
                {t("layout.helpMenu.knowledgeBase")}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!appLinks?.customerSupport}
                onClick={() =>
                  openExternalLink(appLinks?.customerSupport ?? null)
                }
              >
                <MessageCircle className="text-zinc-500" />
                {t("layout.helpMenu.customerSupport")}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <Notifications hotelId={hotelId || ""} enabled={!isPlatformAdmin} />

        <div className={"flex flex-row items-center"}>
          <div className={"flex flex-col items-center justify-center"}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="new_outline"
                  className="h-auto w-56 justify-start py-2"
                >
                  <Avatar>
                    <AvatarFallback>{userInitials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm leading-none font-semibold text-zinc-900">
                      {userName}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-zinc-400">
                      {userRoleLabel}
                    </p>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4 shrink-0 text-zinc-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-3 px-1 py-2">
                    <Avatar>
                      <AvatarFallback>{userInitials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm leading-none font-semibold text-zinc-900">
                        {userName}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-zinc-400">
                        {userRoleLabel}
                      </p>
                      {shouldShowCurrentPlan ? (
                        <p className="mt-1 truncate text-xs text-zinc-500">
                          {t("layout.userMenu.currentPlan", {
                            planTitle: currentPlanTitle,
                          })}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {shouldShowCurrentPlan ? (
                    <DropdownMenuItem disabled>
                      <Sparkles className="text-zinc-500" />
                      {t("layout.userMenu.currentPlan", {
                        planTitle: currentPlanTitle,
                      })}
                    </DropdownMenuItem>
                  ) : null}
                  {shouldShowUpgradeToPro ? (
                    <DropdownMenuItem>
                      <Sparkles className="text-zinc-500" />
                      {t("layout.userMenu.upgradeToPro")}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => navigate("/account")}>
                    <BadgeCheck className="text-zinc-500" />
                    {t("layout.userMenu.account")}
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <CreditCard className="text-zinc-500" />
                    {t("layout.userMenu.billing")}
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Bell className="text-zinc-500" />
                    {t("layout.userMenu.notifications")}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive">
                  <Button
                    variant="new_destructive"
                    onClick={handleAuthAction}
                    className="w-full justify-start"
                  >
                    <LogOut className="text-white" /> {signOutLabel}
                  </Button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};
